import { createContext, useContext, useState, useEffect, ReactNode } from "react";
import { toast } from "sonner";

export interface CartItem {
  id: string;
  productId: string;
  name: string;
  price: number;
  image: string;
  quantity: number;
  variantId?: string;
}

interface CartContextType {
  items: CartItem[];
  addToCart: (item: Omit<CartItem, "quantity">) => void;
  removeFromCart: (id: string) => void;
  updateQuantity: (id: string, quantity: number) => void;
  clearCart: () => void;
  isCartOpen: boolean;
  setIsCartOpen: (open: boolean) => void;
  totalItems: number;
  totalPrice: number;
}

const CartContext = createContext<CartContextType | undefined>(undefined);

const CART_STORAGE_KEY = "gaonova-cart";
const UUID_PATTERN = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;

const isUuid = (value: unknown): value is string =>
  typeof value === "string" && UUID_PATTERN.test(value);

const normalizeStoredCartItems = (storedItems: unknown): CartItem[] => {
  if (!Array.isArray(storedItems)) return [];

  return storedItems.flatMap((storedItem) => {
    if (!storedItem || typeof storedItem !== "object") return [];

    const item = storedItem as Partial<CartItem> & { quantity?: number };
    const productId = isUuid(item.productId) ? item.productId : isUuid(item.id) ? item.id : null;

    if (
      !productId ||
      typeof item.name !== "string" ||
      typeof item.image !== "string" ||
      typeof item.price !== "number" ||
      !Number.isFinite(item.price)
    ) {
      return [];
    }

    const variantId = isUuid(item.variantId) ? item.variantId : undefined;
    const quantity =
      typeof item.quantity === "number" && Number.isFinite(item.quantity) && item.quantity > 0
        ? Math.floor(item.quantity)
        : 1;

    return [
      {
        id:
          typeof item.id === "string" && item.id.trim().length > 0
            ? item.id
            : variantId
              ? `${productId}-${variantId}`
              : productId,
        productId,
        name: item.name,
        price: item.price,
        image: item.image,
        quantity,
        variantId,
      },
    ];
  });
};

export const CartProvider = ({ children }: { children: ReactNode }) => {
  const [items, setItems] = useState<CartItem[]>(() => {
    if (typeof window !== "undefined") {
      try {
        const stored = localStorage.getItem(CART_STORAGE_KEY);
        return stored ? normalizeStoredCartItems(JSON.parse(stored)) : [];
      } catch {
        localStorage.removeItem(CART_STORAGE_KEY);
      }
    }
    return [];
  });
  const [isCartOpen, setIsCartOpen] = useState(false);

  useEffect(() => {
    localStorage.setItem(CART_STORAGE_KEY, JSON.stringify(items));
  }, [items]);

  const addToCart = (item: Omit<CartItem, "quantity">) => {
    // Ensure productId defaults to id if not provided (backward compat)
    const normalizedItem = {
      ...item,
      productId: item.productId || item.id,
    };
    setItems((prev) => {
      const existing = prev.find((i) => i.id === normalizedItem.id);
      if (existing) {
        toast.success(`Added another ${normalizedItem.name}`, {
          description: "The artisan appreciates your continued support!"
        });
        return prev.map((i) =>
          i.id === normalizedItem.id ? { ...i, quantity: i.quantity + 1 } : i
        );
      }
      
      const days = Math.max(2, (normalizedItem.name.length % 5) + 2);
      toast.success(`Added ${normalizedItem.name}`, {
        description: `This piece represents ${days} days of an artisan's livelihood.`
      });
      return [...prev, { ...normalizedItem, quantity: 1 }];
    });
    setIsCartOpen(true);
  };

  const removeFromCart = (id: string) => {
    setItems((prev) => {
      const item = prev.find((i) => i.id === id);
      if (item) {
        toast.info(`Removed ${item.name} from cart`);
      }
      return prev.filter((i) => i.id !== id);
    });
  };

  const updateQuantity = (id: string, quantity: number) => {
    if (quantity < 1) {
      removeFromCart(id);
      return;
    }
    setItems((prev) =>
      prev.map((i) => (i.id === id ? { ...i, quantity } : i))
    );
  };

  const clearCart = () => {
    setItems([]);
    toast.info("Cart cleared");
  };

  const totalItems = items.reduce((sum, item) => sum + item.quantity, 0);
  const totalPrice = items.reduce((sum, item) => sum + item.price * item.quantity, 0);

  return (
    <CartContext.Provider
      value={{
        items,
        addToCart,
        removeFromCart,
        updateQuantity,
        clearCart,
        isCartOpen,
        setIsCartOpen,
        totalItems,
        totalPrice,
      }}
    >
      {children}
    </CartContext.Provider>
  );
};

export const useCart = () => {
  const context = useContext(CartContext);
  if (!context) {
    throw new Error("useCart must be used within a CartProvider");
  }
  return context;
};
